import { BrowserModule } from '@angular/platform-browser';
import { LOCALE_ID, NgModule, Injector, DoBootstrap, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { MomentModule } from 'ngx-moment';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AtAGlanceCollectionsComponent } from './components/at-a-glance/collections/at-a-glance-collections.component';
import { AtAGlancePaymentsComponent } from './components/at-a-glance/payments/at-a-glance-payments.component';
import { AtAGlancePayrollComponent } from './components/at-a-glance/payroll/at-a-glance-payroll.component';
/* i18n stuff is in SharedModule */
import { SharedModule } from './shared/shared.module';
import { SsgPopoverDirective } from './directives/ssg-popover/ssg-popover.directive';
import { PayrollService } from './services/payroll.service';
import { PayGroupService } from './services/paygroup.service';
import { UIPaymentService } from './services/ui-payment.service';
import { PayrollTypeService } from './services/payroll-types.service';
import { CollectionService } from './services/collection.service';
import { PaymentsService } from './services/payments.service';
import { PayService } from './services/pay.service';
import { FiltersService } from './components/dashboard/filters/filters.service';
import { CustomerBankAccountService } from './services/customer-bank-account.service';
import { FeatureToggleService } from './shared/services/feature-toggle/feature-toggle.service';
import { CustomerNotificationService } from './services/customer-notification.service';
import { EmployeePayAppComponent } from './employee-pay-app.component';
import { AtAGlanceComponent } from './components/at-a-glance/at-a-glance.component';
import { AlertsComponent } from './components/alerts/alerts.component';
import { QuickActionsComponent } from './components/quick-actions/quick-actions.component';
import { ResourcesComponent } from './components/resources/resources.component';
import { PayDetailsComponent } from './components/pay-details/pay-details.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { FiltersComponent } from './components/dashboard/filters/filters.component';
import { EntitlementsService } from './entitlements/entitlements.service';
import { TokenInterceptor } from './auth/token.interceptor';
import { HeaderComponent } from './components/header/header.component';
import { WindowRef } from './shared/services/window/window.ref';
import { ErrorComponent } from './components/error/error.component';
import { UnauthorizedQualifiersComponent } from './components/unauthorized-qualifiers/unauthorized-qualifiers.component';
import { Entitlements } from './entitlements/entitlements';
import { AlertsDetailsComponent } from './components/alerts/details/alerts-details.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthCallbackComponent } from './auth/auth-callback.component';
import { OAuthModule } from 'angular-oauth2-oidc';
import { AuthComponent } from './auth/auth.component';
import { LogoutComponent } from './components/logout/logout.component';
import { SystemManagementService } from './services/system-management.service';
import { AuthGuard } from './auth/auth.guard';
import { UltiProAuthService } from './auth/ultiproauth.service';
import { IFrameUtils } from './iframes';
import { CompanyService } from './services/company.service';
import { PrenoteRequestService} from './services/prenote-request.service';
import { CdkTableModule } from '@angular/cdk/table';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { PayInfoTableComponent } from './components/pay-info/pay-info-table/pay-info-table.component';
import { PayInfoFiltersComponent } from './components/pay-info/pay-info-filters/pay-info-filters.component';
import { PayInfoComponent } from './components/pay-info/pay-info.component';
import { PayInfoFiltersService } from './components/pay-info/pay-info-filters/pay-info-filters.service';
import { PayrollScheduleService } from './services/payroll-schedule.service';
import { HeaderService } from './components/header/header.service';
import { createCustomElement } from '@angular/elements';
import { APP_BASE_HREF, LocationStrategy } from '@angular/common';
import { AssetSrcDirective } from './shared/directives/asset-src/asset-src.directive';
import { AssetPathPipe } from './shared/pipes/asset-path.pipe';
import { Util } from '../util';
import { PayInfoActionConfirmationComponent }
from './components/pay-info/pay-info-table/pay-info-action-confirmation/pay-info-action-confirmation.component';
import { PayInfoActionResultsComponent }
from './components/pay-info/pay-info-table/pay-info-action-results/pay-info-action-results.component';
import { BankNotificationCodeService } from './services/bank-notification-code.service';
import { PendoComponent } from './shared/components/pendo/pendo.component';
import { ActionHistoryService } from './services/action-history.service';
import { IgniteModule } from '@ignite/angular';
import { SsgPopoverService } from './services/ssg-popover.service';
import { StaleDatedChecksInfoComponent } from './components/stale-dated-checks-info/stale-dated-checks-info.component';
import { StaleDatedChecksInfoTableComponent }
from './components/stale-dated-checks-info/stale-dated-checks-info-table/stale-dated-checks-info-table.component';
import { StaleDatedCheckFiltersComponent }
from './components/stale-dated-checks-info/stale-dated-check-info-filters/stale-dated-check-info-filters.component';
import { StaleCheckFiltersService }
from './components/stale-dated-checks-info/stale-dated-check-info-filters/stale-dated-check-filters.service';
import { CompanyInfoComponent } from './components/company-info/company-info.component';
import { CompanyInfoTableComponent } from './components/company-info/company-info-table/company-info-table.component';
import { CompanyInfoFiltersService } from './components/company-info/company-info-filters/company-info-filters.service';
import { CompanyInfoFiltersComponent } from './components/company-info/company-info-filters/company-info-filters.component';
import { PrenoteHistoryTableComponent }
from './components/company-info/prenote-history/prenote-history-table/prenote-history-table.component';
import { PrenoteHistoryComponent } from './components/company-info/prenote-history/prenote-history.component';
import { PrenoteHistoryFiltersComponent }
from './components/company-info/prenote-history/prenote-history-filters/prenote-history-filters.component';
import { PrenoteHistoryFiltersService }
from './components/company-info/prenote-history/prenote-history-filters/prenote-history-filters.service';
import { FeatureToggleLaunchDarklyService } from './shared/services/feature-toggle/feature-toggle-ld.service';
import { GatewayUrlInterceptorService } from './shared/interceptors/gateway-url.interceptor';
import { MultiLocationStrategy } from './elements-routing/multilocationstrategy';

function getLocale() {
  const settings = IFrameUtils.getSettingsFromFragment(window.location.hash.substr(1));
  const locale = settings ? settings.locale : navigator.language;

  sessionStorage.setItem('locale', locale);
  return locale;
}

@NgModule({
    declarations: [
        EmployeePayAppComponent,
        AtAGlanceComponent,
        AlertsComponent,
        AlertsDetailsComponent,
        QuickActionsComponent,
        ResourcesComponent,
        PayDetailsComponent,
        FiltersComponent,
        DashboardComponent,
        AtAGlancePayrollComponent,
        AtAGlanceCollectionsComponent,
        AtAGlancePaymentsComponent,
        HeaderComponent,
        ErrorComponent,
        UnauthorizedQualifiersComponent,
        AuthCallbackComponent,
        AuthComponent,
        LogoutComponent,
        PayInfoTableComponent,
        PayInfoActionConfirmationComponent,
        PayInfoFiltersComponent,
        AssetPathPipe,
        PayInfoActionResultsComponent,
        PayInfoComponent,
        AssetSrcDirective,
        SsgPopoverDirective,
        AssetPathPipe,
        StaleDatedChecksInfoComponent,
        StaleDatedChecksInfoTableComponent,
        StaleDatedCheckFiltersComponent,
        CompanyInfoTableComponent,
        CompanyInfoComponent,
        CompanyInfoFiltersComponent,
        PrenoteHistoryTableComponent,
        PrenoteHistoryComponent,
        PrenoteHistoryFiltersComponent
    ],
    imports: [
        BrowserModule,
        MomentModule,
        SharedModule,
        BrowserModule,
        NgSelectModule,
        FormsModule,
        OAuthModule.forRoot(),
        NgbModule,
        AppRoutingModule,
        CdkTableModule,
        ScrollingModule,
        IgniteModule.forRoot()
    ],
    providers: [
        FeatureToggleLaunchDarklyService,
        PayrollService,
        PayrollScheduleService,
        PayGroupService,
        UIPaymentService,
        PayrollTypeService,
        CollectionService,
        SsgPopoverService,
        PaymentsService,
        PayService,
        CompanyService,
        PrenoteRequestService,
        CustomerBankAccountService,
        FeatureToggleService,
        FiltersService,
        PayInfoFiltersService,
        CompanyInfoFiltersService,
        PrenoteHistoryFiltersService,
        HeaderService,
        EntitlementsService,
        SystemManagementService,
        BankNotificationCodeService,
        ActionHistoryService,
        WindowRef,
        { provide: LOCALE_ID, useFactory: () => getLocale() },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: GatewayUrlInterceptorService, multi: true },
        CustomerNotificationService,
        UltiProAuthService,
        AuthGuard,
        Entitlements,
        {
            provide: APP_BASE_HREF,
            useValue: window[Util.HOST_KEY] || ''
        },
        StaleCheckFiltersService,
        {
            provide: LocationStrategy,
            useClass: MultiLocationStrategy,
        }
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: []
})
export class AppModule implements DoBootstrap {
  constructor(private injector: Injector) { }

  ngDoBootstrap(): void {
    this.registerCustomElement(EmployeePayAppComponent, 'ee-pay');
    this.registerCustomElement(AlertsComponent, 'eepay-alerts');
    this.registerCustomElement(PendoComponent, 'eepay-pendo');
  }

  /** Expose component to be accessible as a custom element. */
  registerCustomElement(component: any, tagName: string) {
    const { injector } = this;
    const customElement = createCustomElement(component, { injector });
    customElements.define(tagName, customElement);
  }
}
